// Utility to manage Web Worker for background grid generation

import { getWordsByLength } from './wordDictionary';

let worker: Worker | null = null;
let pendingGenerations = new Map<number, (grid: any) => void>();

export function initGridWorker(): Worker | null {
  if (typeof window === 'undefined') return null;
  
  try {
    worker = new Worker('/gridWorker.js');
    
    worker.onmessage = (e) => {
      const { type, levelId, grid, error } = e.data;
      
      if (type === 'GRID_READY') {
        const callback = pendingGenerations.get(levelId);
        if (callback) {
          callback(grid);
          pendingGenerations.delete(levelId);
        }
      } else if (type === 'ERROR') {
        console.error(`[Worker] ❌ Grid generation error for level ${levelId}:`, error);
        const callback = pendingGenerations.get(levelId);
        if (callback) {
          callback(null); // Signal failure
          pendingGenerations.delete(levelId);
        }
      }
    };
    
    worker.onerror = (error) => {
      console.error('[Worker] ❌ Worker error:', error);
    };
    
    return worker;
  } catch (error) {
    console.warn('[Worker] ⚠️ Web Workers not supported, falling back to synchronous generation:', error);
    return null;
  }
}

export function preGenerateLevel(levelId: number, onComplete: (grid: any) => void): void {
  if (!worker) {
    onComplete(null);
    return;
  }
  
  // Store callback first
  pendingGenerations.set(levelId, onComplete);
  
  // Defer dictionary building to avoid blocking UI during level transitions
  // Use requestIdleCallback if available, otherwise setTimeout with 0
  const defer = (callback: () => void) => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      (window as any).requestIdleCallback(callback, { timeout: 50 });
    } else {
      setTimeout(callback, 0);
    }
  };
  
  defer(() => {
    // Check worker is still available (could be null if cleanup happened)
    if (!worker) {
      const callback = pendingGenerations.get(levelId);
      if (callback) {
        callback(null);
        pendingGenerations.delete(levelId);
      }
      return;
    }
    
    // Build word dictionary for worker
    const wordDictionary: { [length: number]: string[] } = {};
    for (let len = 3; len <= 8; len++) {
      wordDictionary[len] = getWordsByLength(len);
    }
    
    // Request generation
    worker.postMessage({
      type: 'GENERATE_GRID',
      levelId,
      wordDictionary
    });
  });
}

export function cleanupWorker(): void {
  if (worker) {
    worker.terminate();
    worker = null;
    pendingGenerations.clear();
  }
}

